import DataLoader from 'dataloader'

import request from '../requesters/clientPortal'
import oathKeeper from '../requesters/oathKeeper'
import type { CdmGetCaseResponse, CdmUpdateCaseResponse } from '../service/luther'
import type { UUID } from '../types'
import { formatCase } from '../utils/schemaMapping/case'

const fetchCasesBatchFn = async (ids: UUID[]) => {
  const caseIds = ids.join('&case_ids=')
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const requester = useOathKeeper === 'true' ? oathKeeper : request
  const response = await requester.get<CdmGetCaseResponse>(`/case?case_ids=${caseIds}&case_details=true`)

  return response.data.cases?.map(formatCase) || []
}

export const CaseLoaderClient = new DataLoader(async (keys: { id: string }[]) => {
  const ids = keys.map((key) => key.id)
  const cases = await fetchCasesBatchFn(ids)
  const data = ids.map((id) => cases.find((caseData) => caseData.id === id) || null)

  return data
})

export const fetchCase = async (id: UUID) => {
  return await CaseLoaderClient.load({ id })
}

export const fetchCasesForClient = async (clientId: string) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const requester = useOathKeeper === 'true' ? oathKeeper : request

  const response = await requester.get<CdmGetCaseResponse>(`/case?case_details=false&filter_client_ids=${clientId}`)

  const caseIds = response.data.cases?.reduce((acc, caseData) => {
    if (caseData.case_id) {
      acc.push(caseData.case_id)
    }

    return acc
  }, [] as string[])

  if (!caseIds) {
    return []
  }

  const cases = await CaseLoaderClient.loadMany(caseIds.map((id) => ({ id })))

  return cases.filter((caseData) => caseData !== null && caseData.details.status !== 'REFERRED') as CaseVersion[]
}

export const updateCaseCp = async (input: CaseInput, id: string) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const requester = useOathKeeper === 'true' ? oathKeeper : request

  const response = await requester.patch<CdmUpdateCaseResponse>(`/case/${id}`, {
    details: input,
  })

  CaseLoaderClient.clear({ id })

  return response.data.case ? formatCase(response.data.case) : null
}

export const createLeadCaseReferral = async (
  case_id: string,
  client_ids: string[],
  preference_mortgage_reason: string,
  target_user_id?: string,
  client_generated_lead?: boolean,
) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const requester = useOathKeeper === 'true' ? oathKeeper : request

  const response = await requester.post(`/case/referral`, {
    case_id,
    client_ids,
    preference_mortgage_reason,
    target_user_id,
    client_generated_lead,
  })

  return response
}
